/**
 * Analytics Service
 * 
 * Main analytics wrapper service that provides a clean, provider-agnostic API.
 * This service never imports PostHog directly - it only knows about provider interfaces.
 * 
 * Usage:
 *   import analyticsService from './services/analyticsService';
 *   analyticsService.track('event_name', { property: 'value' });
 */

import BaseProvider from './analytics/providers/baseProvider';
import PostHogProvider from './analytics/providers/posthogProvider';

class AnalyticsService {
  constructor() {
    this.currentProvider = null;
    this.isEnabled = true;
    this.isInitialized = false;
    
    // Provider registry - maps provider names to their implementations
    this.providers = {
      posthog: PostHogProvider
      // Future providers can be added here:
      // mixpanel: MixpanelProvider,
      // amplitude: AmplitudeProvider
    };
  }

  /**
   * Initialize analytics with a provider
   * @param {Object} options - Initialization options
   * @param {string} options.provider - Provider name (e.g., 'posthog')
   * @param {Object} options.config - Provider-specific configuration
   */
  init({ provider, config }) {
    try {
      // Prevent double initialization (React StrictMode in dev causes double renders)
      if (this.isInitialized) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Analytics already initialized, skipping re-initialization');
        }
        return;
      }

      // Check if analytics is enabled
      const enabled = process.env.REACT_APP_ANALYTICS_ENABLED;
      if (enabled === 'false') {
        this.isEnabled = false;
        console.log('Analytics disabled via REACT_APP_ANALYTICS_ENABLED');
        return;
      }
      
      // Check if API key is provided
      if (!config?.apiKey) {
        console.warn('Analytics: API key not provided. Analytics will not initialize.');
        console.warn('Set REACT_APP_ANALYTICS_KEY in environment variables.');
        return;
      }

      // Get provider class from registry
      const ProviderClass = this.providers[provider];
      if (!ProviderClass) {
        console.error(`Analytics provider "${provider}" not found in registry`);
        return;
      }

      // Expose to window BEFORE initializing provider
      // This ensures the reference is available when provider's loaded callback fires
      // Available in all environments so PostHog callback can access it
      window.analyticsService = this;
      
      // Instantiate and initialize provider
      this.currentProvider = new ProviderClass();
      
      // Initialize provider (this is async for PostHog)
      this.currentProvider.init(config);
      
      // Don't set isInitialized here - wait for provider to be ready
      // For PostHog, this happens in the loaded callback
      
      // For providers that initialize synchronously, we need to check
      // But PostHog is async, so we'll rely on the provider's isInitialized flag
      // We can't set this.isInitialized = true here because PostHog isn't ready yet
    } catch (error) {
      console.error('Analytics initialization error:', error);
      // Don't break the app if analytics fails
      this.currentProvider = null;
    }
  }

  /**
   * Get service worker cache version
   * Checks which cache version the service worker is using
   */
  _getCacheVersion() {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return 'no-sw';
    }
    
    // Try to get cache version from service worker registration
    // This is async, so we'll use a cached value or check available caches
    try {
      // Check if we have a cached version (set when service worker activates)
      const cachedVersion = sessionStorage.getItem('sw_cache_version');
      if (cachedVersion) {
        return cachedVersion;
      }
      
      // Try to get from caches API (async, but we'll return a promise result)
      // For now, return the expected version based on current deployment
      // This should match CACHE_NAME in public/sw.js
      return 'theraengage-v9'; // Update this when you bump cache version
    } catch (error) {
      return 'unknown';
    }
  }

  /**
   * Helper method to enrich properties with environment tag and cache version
   * @param {Object} properties - Original properties
   * @returns {Object} - Properties with environment tag and cache version added
   */
  _getEnrichedProperties(properties = {}) {
    const environment = process.env.REACT_APP_ANALYTICS_ENVIRONMENT || 'production';
    const cacheVersion = this._getCacheVersion();
    
    return {
      ...properties,
      environment: environment,
      cacheVersion: cacheVersion
    };
  }

  /**
   * Track a custom event
   * @param {string} eventName - Name of the event
   * @param {Object} properties - Optional event properties
   * @returns {boolean} - Returns true if event was tracked successfully, false otherwise
   */
  track(eventName, properties = {}) {
    if (!this.isEnabled || !this.currentProvider) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Analytics: Cannot track event - analytics not initialized or disabled');
      }
      return false;
    }

    try {
      const enrichedProperties = this._getEnrichedProperties(properties);
      const result = this.currentProvider.track(eventName, enrichedProperties);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ Analytics: Tracked event "${eventName}"`, enrichedProperties);
      }
      // Return the result from the provider (could be a promise, boolean, or other value)
      return result !== undefined ? result : true;
    } catch (error) {
      console.error('Analytics track error:', error);
      // Don't break the app
      return false;
    }
  }

  /**
   * Track a page view
   * @param {string} pageName - Name of the page
   * @param {Object} properties - Optional page properties
   * @returns {boolean} - Returns true if page view was tracked successfully, false otherwise
   */
  page(pageName, properties = {}) {
    if (!this.isEnabled || !this.currentProvider) {
      return false;
    }

    try {
      const enrichedProperties = this._getEnrichedProperties(properties);
      const result = this.currentProvider.page(pageName, enrichedProperties);
      // Return the result from the provider (could be a promise, boolean, or other value)
      return result !== undefined ? result : true;
    } catch (error) {
      console.error('Analytics page error:', error);
      // Don't break the app
      return false;
    }
  }

  /**
   * Identify a user
   * @param {string} userId - Unique user identifier
   * @param {Object} properties - Optional user properties
   * @returns {boolean} - Returns true if user was identified successfully, false otherwise
   */
  identify(userId, properties = {}) {
    if (!this.isEnabled || !this.currentProvider) {
      return false;
    }

    try {
      const enrichedProperties = this._getEnrichedProperties(properties);
      const result = this.currentProvider.identify(userId, enrichedProperties);
      // Return the result from the provider (could be a promise, boolean, or other value)
      return result !== undefined ? result : true;
    } catch (error) {
      console.error('Analytics identify error:', error);
      // Don't break the app
      return false;
    }
  }

  /**
   * Set or update user properties
   * @param {Object} properties - User properties to set
   */
  setUserProperties(properties) {
    if (!this.isEnabled || !this.currentProvider) {
      return;
    }

    try {
      this.currentProvider.setUserProperties(properties);
    } catch (error) {
      console.error('Analytics setUserProperties error:', error);
      // Don't break the app
    }
  }

  /**
   * Associate user with a group/organization
   * @param {string} groupId - Group identifier
   * @param {Object} traits - Optional group properties
   */
  group(groupId, traits = {}) {
    if (!this.isEnabled || !this.currentProvider) {
      return;
    }

    try {
      this.currentProvider.group(groupId, traits);
    } catch (error) {
      console.error('Analytics group error:', error);
      // Don't break the app
    }
  }

  /**
   * Merge user identities (anonymous → identified)
   * @param {string} newId - New user identifier
   * @param {string} originalId - Original user identifier
   */
  alias(newId, originalId) {
    if (!this.isEnabled || !this.currentProvider) {
      return;
    }

    try {
      this.currentProvider.alias(newId, originalId);
    } catch (error) {
      console.error('Analytics alias error:', error);
      // Don't break the app
    }
  }

  /**
   * Reset user session (logout)
   */
  reset() {
    if (!this.currentProvider) {
      return;
    }

    try {
      this.currentProvider.reset();
    } catch (error) {
      console.error('Analytics reset error:', error);
      // Don't break the app
    }
  }

  /**
   * Flush queued events immediately
   */
  flush() {
    if (!this.currentProvider) {
      return;
    }

    try {
      this.currentProvider.flush();
    } catch (error) {
      console.error('Analytics flush error:', error);
      // Don't break the app
    }
  }

  /**
   * Check if a feature flag is enabled (provider-specific)
   * @param {string} flagName - Feature flag name
   * @returns {boolean|null} - Returns true/false if supported, null otherwise
   */
  isFeatureEnabled(flagName) {
    if (!this.isEnabled || !this.currentProvider) {
      return null;
    }

    try {
      return this.currentProvider.isFeatureEnabled(flagName);
    } catch (error) {
      console.error('Analytics isFeatureEnabled error:', error);
      return null;
    }
  }

  /**
   * Get feature flag value (provider-specific)
   * @param {string} flagName - Feature flag name
   * @returns {any|null} - Returns flag value if supported, null otherwise
   */
  getFeatureFlag(flagName) {
    if (!this.isEnabled || !this.currentProvider) {
      return null;
    }

    try {
      return this.currentProvider.getFeatureFlag(flagName);
    } catch (error) {
      console.error('Analytics getFeatureFlag error:', error);
      return null;
    }
  }
}

// Export singleton instance
const analyticsService = new AnalyticsService();
export default analyticsService;

